import type { QuestEvent, QuestPeriod } from './config';


export interface QuestVariantCopy {
  title: string;
  one: string;
  other: string;
}

export interface QuestObjectiveDef {
  event: QuestEvent;
  weight: number;
  periods: readonly QuestPeriod[];
  
  scale: number;
  fixed?: number;
  minEffort?: number;
  maxEffort?: number;
  variants: readonly [QuestVariantCopy, QuestVariantCopy, QuestVariantCopy];
}

function v(title: string, one: string, other: string): QuestVariantCopy {
  return { title, one, other };
}

export const QUEST_OBJECTIVES: readonly QuestObjectiveDef[] = [
  {
    event: 'booster_opened',
    weight: 14,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 1,
    variants: [
      v('Ouvrir des boosters', 'Ouvre 1 booster, gratuit ou payant.', 'Ouvre {n} boosters, gratuits ou payants.'),
      v('Toutes pochettes', 'Ouvre 1 booster, quel que soit son type.', 'Ouvre {n} boosters, quel que soit leur type.'),
      v('Compteur d’ouvertures', 'Totalise 1 booster ouvert.', 'Totalise {n} boosters ouverts.'),
    ],
  },
  {
    event: 'character_new',
    weight: 10,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.25,
    variants: [
      v('Nouveaux personnages', 'Obtiens 1 personnage que tu ne possédais pas.', 'Obtiens {n} personnages que tu ne possédais pas.'),
      v('Nouvelles têtes', 'Ajoute 1 nouveau personnage à ton classeur.', 'Ajoute {n} nouveaux personnages à ton classeur.'),
      v('Cases vierges', 'Remplis 1 case vide de ta collection.', 'Remplis {n} cases vides de ta collection.'),
    ],
  },
  {
    event: 'character_duplicate',
    weight: 8,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.34,
    variants: [
      v('Doublons', 'Obtiens 1 personnage que tu possèdes déjà.', 'Obtiens {n} personnages que tu possèdes déjà.'),
      v('Déjà croisés', 'Retombe sur 1 personnage déjà possédé.', 'Retombe sur {n} personnages déjà possédés.'),
      v('Copies en trop', 'Accumule 1 doublon de carte.', 'Accumule {n} doublons de cartes.'),
    ],
  },
  {
    event: 'auction_bid',
    weight: 5,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.125,
    variants: [
      v('Enchères', 'Crée une enchère ou fais 1 enchère.', 'Crée ou fais {n} enchères.'),
      v('Main sur le marteau', 'Lance ou relance 1 enchère.', 'Lance ou relance {n} enchères.'),
      v('Salle des ventes', 'Participe à 1 enchère.', 'Participe à {n} enchères.'),
    ],
  },
  {
    event: 'collection_viewed',
    weight: 7,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 8,
    variants: [
      v('Consulter ta collection', 'Ouvre la page de ta collection 1 fois.', 'Ouvre la page de ta collection 1 fois.'),
      v('Tour du classeur', 'Feuillette ta collection 1 fois.', 'Feuillette ta collection 1 fois.'),
      v('Inventaire', 'Consulte tes cartes 1 fois.', 'Consulte tes cartes 1 fois.'),
    ],
  },
  {
    event: 'free_booster_opened',
    weight: 8,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.5,
    variants: [
      v('Booster gratuit', 'Ouvre 1 booster gratuit.', 'Ouvre {n} boosters gratuits.'),
      v('Réserve du jour', 'Dépense 1 charge de ta réserve gratuite.', 'Dépense {n} charges de ta réserve gratuite.'),
      v('Pochettes offertes', 'Ouvre 1 pochette gratuite.', 'Ouvre {n} pochettes gratuites.'),
    ],
  },
  {
    event: 'premium_booster_opened',
    weight: 6,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.2,
    minEffort: 6,
    variants: [
      v('Booster Premium', 'Ouvre 1 booster Premium.', 'Ouvre {n} boosters Premium.'),
      v('Pochettes dorées', 'Ouvre 1 pochette Premium.', 'Ouvre {n} pochettes Premium.'),
      v('Au-dessus du lot', 'Tente 1 ouverture Premium.', 'Tente {n} ouvertures Premium.'),
    ],
  },
  {
    event: 'legendary_booster_opened',
    weight: 3,
    periods: ['weekly', 'monthly'],
    scale: 0.04,
    minEffort: 30,
    variants: [
      v('Booster Chanceux', 'Ouvre 1 booster Chanceux.', 'Ouvre {n} boosters Chanceux.'),
      v('Pochettes rares', 'Ouvre 1 pochette Chanceux.', 'Ouvre {n} pochettes Chanceux.'),
      v('Coup de chance', 'Tente 1 ouverture Chanceux.', 'Tente {n} ouvertures Chanceux.'),
    ],
  },
  {
    event: 'rare_or_better_obtained',
    weight: 7,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.2,
    variants: [
      v('Rare ou mieux', 'Obtiens 1 personnage Rare ou de rareté supérieure.', 'Obtiens {n} personnages Rares ou de rareté supérieure.'),
      v('Éclats de couleur', 'Tire 1 carte Rare, Épique, Légendaire, Mythique ou Divine.', 'Tire {n} cartes Rares ou mieux.'),
      v('Au-dessus du commun', 'Décroche 1 rareté Rare ou plus.', 'Décroche {n} raretés Rare ou plus.'),
    ],
  },
  {
    event: 'epic_or_better_obtained',
    weight: 4,
    periods: ['weekly', 'monthly'],
    scale: 0.05,
    minEffort: 20,
    variants: [
      v('Épique ou mieux', 'Obtiens 1 personnage Épique ou de rareté supérieure.', 'Obtiens {n} personnages Épique ou de rareté supérieure.'),
      v('Halo violet', 'Tire 1 carte Épique, Légendaire, Mythique ou Divine.', 'Tire {n} cartes Épique ou mieux.'),
      v('Grand cru', 'Décroche 1 rareté Épique ou plus.', 'Décroche {n} raretés Épique ou plus.'),
    ],
  },
  {
    event: 'card_upgraded',
    weight: 6,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.15,
    variants: [
      v('Améliorer des cartes', 'Améliore une carte 1 fois.', 'Améliore des cartes {n} fois.'),
      v('Atelier', 'Monte 1 carte d’un niveau.', 'Monte des cartes de {n} niveaux au total.'),
      v('Montée en gamme', 'Dépense 1 amélioration de carte.', 'Dépense {n} améliorations de cartes.'),
    ],
  },
  {
    event: 'fragments_spent',
    weight: 5,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 8,
    variants: [
      v('Fragments dépensés', 'Dépense 1 fragment en améliorations de cartes.', 'Dépense {n} fragments en améliorations de cartes.'),
      v('Fourneau', 'Brûle 1 fragment dans la forge.', 'Brûle {n} fragments dans la forge.'),
      v('Investissement', 'Investis 1 fragment dans une carte.', 'Investis {n} fragments dans tes cartes.'),
    ],
  },
  {
    event: 'fragments_earned',
    weight: 5,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 10,
    variants: [
      v('Fragments gagnés', 'Gagne 1 fragment.', 'Gagne {n} fragments.'),
      v('Récolte d’éclats', 'Ramasse 1 fragment.', 'Ramasse {n} fragments.'),
      v('Butin cristallin', 'Accumule 1 fragment.', 'Accumule {n} fragments.'),
    ],
  },
  {
    event: 'listing_created',
    weight: 3,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.1,
    variants: [
      v('Annonces publiées', 'Publie 1 annonce sur le marché.', 'Publie {n} annonces sur le marché.'),
      v('Cartes en vitrine', 'Mets 1 carte en vente.', 'Mets {n} cartes en vente.'),
      v('Prix affiché', 'Crée 1 annonce de vente.', 'Crée {n} annonces de vente.'),
    ],
  },
  {
    event: 'market_purchase',
    weight: 3,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.1,
    variants: [
      v('Achats au marché', 'Achète 1 carte sur le marché.', 'Achète {n} cartes sur le marché.'),
      v('Bonne affaire', 'Conclus 1 achat au marché.', 'Conclus {n} achats au marché.'),
      v('Panier', 'Acquiers 1 carte via le marché.', 'Acquiers {n} cartes via le marché.'),
    ],
  },
  {
    event: 'trade_proposed',
    weight: 3,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.08,
    variants: [
      v('Échanges proposés', 'Propose 1 échange.', 'Propose {n} échanges.'),
      v('Main tendue', 'Envoie 1 offre d’échange.', 'Envoie {n} offres d’échange.'),
      v('Table de négoce', 'Ouvre 1 négociation d’échange.', 'Ouvre {n} négociations d’échange.'),
    ],
  },
  {
    event: 'trade_completed',
    weight: 3,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.08,
    variants: [
      v('Échanges conclus', 'Conclus 1 échange.', 'Conclus {n} échanges.'),
      v('Marché conclu', 'Scelle 1 échange accepté.', 'Scelle {n} échanges acceptés.'),
      v('Poignée finale', 'Termine 1 troc.', 'Termine {n} trocs.'),
    ],
  },
  {
    event: 'system_sale',
    weight: 2,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.1,
    variants: [
      v('Reventes au système', 'Revends 1 carte au système.', 'Revends {n} cartes au système.'),
      v('Faire de la place', 'Laisse partir 1 carte au système.', 'Laisse partir {n} cartes au système.'),
      v('Reprise instantanée', 'Cède 1 carte au rachat du système.', 'Cède {n} cartes au rachat du système.'),
    ],
  },
  {
    event: 'anima_spent',
    weight: 3,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 20,
    variants: [
      v('Anima dépensée', 'Dépense 1 Anima.', 'Dépense {n} Anima.'),
      v('Bourse ouverte', 'Laisse filer 1 Anima.', 'Laisse filer {n} Anima.'),
      v('Mise en jeu', 'Engage 1 Anima.', 'Engage {n} Anima.'),
    ],
  },
  {
    event: 'message_sent',
    weight: 4,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.4,
    variants: [
      v('Messages envoyés', 'Envoie 1 message privé.', 'Envoie {n} messages privés.'),
      v('Prise de parole', 'Écris 1 message à un ami.', 'Écris {n} messages à tes amis.'),
      v('Fil de discussion', 'Poste 1 message.', 'Poste {n} messages.'),
    ],
  },
  {
    event: 'card_shared',
    weight: 2,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.15,
    variants: [
      v('Cartes partagées', 'Partage 1 carte dans une discussion.', 'Partage {n} cartes dans des discussions.'),
      v('Montrer son jeu', 'Montre 1 carte dans un message.', 'Montre {n} cartes dans tes messages.'),
      v('Carte postale', 'Envoie 1 carte à un ami.', 'Envoie {n} cartes à tes amis.'),
    ],
  },
  {
    event: 'reaction_given',
    weight: 2,
    periods: ['daily', 'weekly', 'monthly'],
    scale: 0.2,
    variants: [
      v('Réactions', 'Réagis 1 fois à un message.', 'Réagis {n} fois à des messages.'),
      v('Petit signe', 'Laisse 1 réaction.', 'Laisse {n} réactions.'),
      v('Émoji', 'Ajoute 1 réaction à un message.', 'Ajoute {n} réactions à des messages.'),
    ],
  },
  {
    event: 'friend_request_sent',
    weight: 2,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 12,
    variants: [
      v('Demande d’ami', 'Envoie 1 demande d’ami.', 'Envoie 1 demande d’ami.'),
      v('Nouveau contact', 'Tends la main à 1 joueur.', 'Tends la main à 1 joueur.'),
      v('Invitation', 'Invite 1 joueur comme ami.', 'Invite 1 joueur comme ami.'),
    ],
  },
  {
    event: 'profile_viewed',
    weight: 3,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 8,
    variants: [
      v('Profils visités', 'Ouvre le profil d’un autre joueur.', 'Ouvre le profil d’un autre joueur.'),
      v('Curiosité', 'Va voir 1 profil de joueur.', 'Va voir 1 profil de joueur.'),
      v('Passage', 'Consulte 1 page de joueur.', 'Consulte 1 page de joueur.'),
    ],
  },
  {
    event: 'collection_compared',
    weight: 2,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 10,
    variants: [
      v('Comparer une collection', 'Compare ta collection à celle d’un autre joueur.', 'Compare ta collection à celle d’un autre joueur.'),
      v('Face à face', 'Mets ton classeur en regard d’un autre.', 'Mets ton classeur en regard d’un autre.'),
      v('Qui a quoi', 'Regarde ce qui te manque chez un joueur.', 'Regarde ce qui te manque chez un joueur.'),
    ],
  },
  {
    event: 'showcase_edited',
    weight: 2,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 8,
    variants: [
      v('Vitrine modifiée', 'Modifie ta vitrine 1 fois.', 'Modifie ta vitrine 1 fois.'),
      v('Nouvelle devanture', 'Réorganise tes cartes exposées.', 'Réorganise tes cartes exposées.'),
      v('Étagère', 'Retouche un emplacement de vitrine.', 'Retouche un emplacement de vitrine.'),
    ],
  },
  {
    event: 'favorite_added',
    weight: 3,
    periods: ['daily'],
    scale: 0.25,
    maxEffort: 12,
    variants: [
      v('Nouveaux favoris', 'Mets 1 personnage en favori.', 'Mets {n} personnages en favori.'),
      v('Étoile', 'Épingle 1 carte en favori.', 'Épingle {n} cartes en favori.'),
      v('Coup de cœur', 'Marque 1 personnage comme favori.', 'Marque {n} personnages comme favoris.'),
    ],
  },
  {
    event: 'cosmetic_equipped',
    weight: 2,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 8,
    variants: [
      v('Changer de style', 'Équipe 1 cosmétique.', 'Équipe 1 cosmétique.'),
      v('Nouvelle allure', 'Porte un titre, un badge, un cadre ou un filtre.', 'Porte un titre, un badge, un cadre ou un filtre.'),
      v('Essayage', 'Applique 1 cosmétique à ton profil.', 'Applique 1 cosmétique à ton profil.'),
    ],
  },
  {
    event: 'login_claimed',
    weight: 4,
    periods: ['daily'],
    scale: 1,
    fixed: 1,
    maxEffort: 10,
    variants: [
      v('Connexion du jour', 'Réclame la récompense de connexion du calendrier.', 'Réclame la récompense de connexion du calendrier.'),
      v('Présent', 'Pointe au calendrier 1 fois.', 'Pointe au calendrier 1 fois.'),
      v('Rituel', 'Récupère le bonus quotidien du calendrier.', 'Récupère le bonus quotidien du calendrier.'),
    ],
  },
  {
    event: 'anime_completed',
    weight: 2,
    periods: ['weekly', 'monthly'],
    scale: 0.02,
    minEffort: 40,
    variants: [
      v('Animes complétés', 'Complète 1 anime.', 'Complète {n} animes.'),
      v('Série bouclée', 'Termine 1 série.', 'Termine {n} séries.'),
      v('Cent pour cent', 'Boucle 1 anime à 100 %.', 'Boucle {n} animes à 100 %.'),
    ],
  },
  {
    event: 'achievement_unlocked',
    weight: 2,
    periods: ['weekly', 'monthly'],
    scale: 0.04,
    minEffort: 30,
    variants: [
      v('Succès débloqués', 'Débloque 1 succès.', 'Débloque {n} succès.'),
      v('Trophées', 'Décroche 1 trophée.', 'Décroche {n} trophées.'),
      v('Tableau d’honneur', 'Ajoute 1 succès à ton tableau.', 'Ajoute {n} succès à ton tableau.'),
    ],
  },
  {
    event: 'quest_claimed',
    weight: 3,
    periods: ['daily'],
    scale: 0.2,
    maxEffort: 12,
    variants: [
      v('Quêtes réclamées', 'Réclame 1 autre quête terminée.', 'Réclame {n} autres quêtes terminées.'),
      v('Récompense encaissée', 'Encaisse 1 quête terminée.', 'Encaisse {n} quêtes terminées.'),
      v('Tableau allégé', 'Valide 1 quête.', 'Valide {n} quêtes.'),
    ],
  },
];

const BY_EVENT = new Map(QUEST_OBJECTIVES.map((objective) => [objective.event, objective]));

export function questObjective(event: QuestEvent): QuestObjectiveDef | undefined {
  return BY_EVENT.get(event);
}
